import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { Router } from '@angular/router';
import { MenuItem, MenuItemCommandEvent } from 'primeng/api';

import { AppRouteConstant } from '../common/app-route.constant';
import { AppConstant } from '../common/app.constant';
import { ChangeRequestType } from '../models/change-request-type.enum';
import { ChangeRequest } from '../models/changerequest';
import { Comment } from '../models/comment';
import { CommentType } from '../models/commenttype.enum';
import { Direction } from '../models/direction.enum';
import { DisplayStatus } from '../models/displaystatus.enum';
import { EICSTatus } from '../models/eic-status.enum';
import { EmployeeInCharge } from '../models/employeeInCharge';
import { Location } from '../models/location';
import { RejectedModification } from '../models/rejected-modification.modal';
import { SecondaryEICModificationStatus } from '../models/secondary-eic-modification-status.enum';
import { ShareInfoUsersListModal } from '../models/share-info.users-list.modal';
import { Source } from '../models/source.enum';
import { Status } from '../models/status.enum';
import { ITrackAuthority, TrackAuthority } from '../models/trackauthority';
import { UserInfo } from '../models/user-info.modal';
import { ChangeRequestService } from '../services/changerequest.service';
import { DataShareService } from '../services/data-share.service';
import { DataService } from '../services/data.service';
import { ATMEventData, EventBusService, EventType } from '../services/eventbus.service';
import { SystemStatusService } from '../services/systemstatus.service';
import { UtilService } from '../services/util.service';
import { ChangeRequestComment } from '../models/changerequestcomment';
import { ChangeRequestCommentType } from '../models/changerequestcommenttype.enum';
import { ProxyUser } from '../models/proxyuser';
import { User, AuthenticationService } from '@csx/security';

@Component({
  selector: 'authority-row',
  templateUrl: './authority.component.html',
  styleUrls: ['./authority.component.scss']
})
export class AuthorityComponent implements OnChanges {
  // Need to use the enum in template
  Direction = Direction;
  DisplayStatus = DisplayStatus;
  eicStatus = EICSTatus;
  Status = Status;
  Source = Source;
  SecondaryEICModificationStatus = SecondaryEICModificationStatus;
  @Input() authority: TrackAuthority;
  @Input() actionTableVisible = true;
  @Input() showActionButtons = true;
  @Input() isAuthorityHistory = false;
  @Input() lastUpdated: Date;
  @Input() authorityMenu: MenuItem[];
  @Input() isSharedModificationRejected: boolean;
  @Input() systemStateStatus;
  @Input() isAuthorityCard: boolean;
  @Output() actionRequested: EventEmitter<Status | Source> = new EventEmitter();
  @Output() commentAuthority: EventEmitter<TrackAuthority> = new EventEmitter();
  @Output() copyAndResubmit: EventEmitter<TrackAuthority> = new EventEmitter();
  @Output() changeRequest: EventEmitter<TrackAuthority> = new EventEmitter();
  disable = false;
  operatedUpon: boolean;
  updated: boolean;
  firstActiveLineItemIndex: number;
  releaseSharedAuthorityModelStatus: boolean = false;
  isSharedAutority: boolean = false;
  changeRequestPending = false;
  releaseEicList: EmployeeInCharge[];
  public statusArray: Status[] = [
    Status.CHANGE_REQUEST,
    Status.CHANGE_SENT,
    Status.CHANGE_PENDING,
    Status.CHANGE_USER_REJECTED,
    Status.CHANGE_USER_APPROVED,
    Status.CHANGE_PROPOSED,
    Status.CHANGE_PROPOSED_PARTIALLY_ACCEPTED,
    Status.CHANGE_PROPOSED_ACCEPTED
  ];
  showActionTable: boolean;
  shareInfoPending: ShareInfoUsersListModal;
  shareInfoActive: ShareInfoUsersListModal;
  shareInfoReviewRequest: ShareInfoUsersListModal;

  showShareInfo: boolean;
  enableReleaseShareAuthorityButton: boolean;
  loggedInUserId: string;
  isReviewButtonEnable: boolean;
  isReviewSharedButtonEnable: boolean;

  authorityChangeRequest: ChangeRequest;
  isSecondaryRequestPending: boolean;
  showReviewModificationButton: boolean;
  reviewSharingRequest: boolean;
  rejectedModifications: RejectedModification[];
  hideShareButton: boolean;
  private activeCount = 0;
  private pendingCount = 0;
  sharedAwaitingReponse: boolean;
  isMobileViewPendingStatus: boolean;
  AppConstant = AppConstant;
  isTTPSuccessFul: boolean;

  ngOnChanges() {
    if (!this.isAuthorityCard) {
      this.resetSharedFlags();
      this.hideShareButton = this.authority.sourceSystemName === Source.TRANSFER_TO_PAPER || this.authority.status === Status.RELEASED;
      this.setSharedData();
      this.sharedAwaitingReponse =
        (this.isSecondaryRequestPending && !this.authority.isLoggedUserPrimary) ||
        (!this.authority.isLoggedUserPrimary &&
          this.authority.modifyReportbyChangeRequest.authorityChangeRequestStatus === Status.CHANGE_DISPATCHER_APPROVED.toString());

      // TODO we might cache all other calculations in future
      this.firstActiveLineItemIndex = this.authority.lineItems.findIndex(lineItem => lineItem.cancelled === undefined);
      this.isMobileViewPendingStatus = this.checkMobileViewPendingStatus();
      this.hideShowAuthorityMenuItem();
    }
  }

  constructor(
    private router: Router,
    private eventBus: EventBusService,
    private dataService: DataService,
    private _systemStatusService: SystemStatusService,
    private changeRequestService: ChangeRequestService,
    private dataShareService: DataShareService,
    private proxy: ProxyUser,
    private user: User,
    private authService: AuthenticationService
  ) {
    this.loggedInUserId = this.proxy.racf || this.user.racf;
    eventBus
      .getEventBus(EventType.AUTHORITY_UPDATED_EVENT)
      .subscribe(response => this.handleUpdateSuccess(response.data, response.disableFlash));
    this.releaseEicList = [];
  }

  get systemState(): SystemStatusService {
    return this._systemStatusService;
  }

  getLine11Items(): Array<Comment> {
    return this.authority.comments.filter(comment => UtilService.isCommentOfType(comment, [CommentType.LINE11]));
  }

  getCommentList(): Array<Comment> {
    return UtilService.getFirstCommentOfTypes(this.authority.comments, [CommentType.DISPATCHER, CommentType.PRIVATE]);
  }

  copyAndResubmitAuthority(): void {
    this.copyAndResubmit.emit(this.authority);
  }

  handleConfirmAction(): void {
    if (this.authority.status === Status.AUTHORITY_APPROVED) {
      this.dataService.updateAuthorityStatus(this.authority, Status.AUTHORITY_USER_REJECTED);
      this.operatedUpon = false;
    } else if (this.authority.status === Status.RELEASE_APPROVED) {
      this.dataService.updateAuthorityStatus(this.authority, Status.RELEASE_USER_REJECTED);
      this.operatedUpon = false;
    } else if (this.authority.status === Status.ACTIVE && this.authority.modifyReportbyChangeRequest.authorityChangeRequestId) {
      this.dataService.updateChangeRequestStatus(this.authority, Status.CHANGE_USER_REJECTED).then(success => {
        this.eventBus.fireEvent(EventType.AUTHORITY_UPDATED_EVENT, new ATMEventData(this.authority, () => { }));
      });
      this.operatedUpon = false;
    }
  }

  reviewSharedAuthority() {
    const item = { authorityId: this.authority.id, authority: this.authority };
    this.dataService.openSharedAuthorityModel(true, AppConstant.AUTHORITY_SHARED, item);
  }

  reviewAuthority() {
    this.releaseEicList = [];
    this.authority.employeeInCharges.forEach(eic => {
      if (
        eic.status === EICSTatus.PENDING &&
        !eic.primary &&
        this.authority.eicChangeRequests[eic.eicUserId].authorityChangeRequestStatus &&
        this.authority.eicChangeRequests[eic.eicUserId].authorityChangeRequestStatus !== Status.SHARING_INITIATED
      ) {
        eic.selected = 0;
        this.releaseEicList.push(eic);
      }
    });
    this.releaseSharedAuthorityModelStatus = true;
    this.reviewSharingRequest = true;
  }

  reviewModification() {
    const item = { authorityId: this.authority.id, authority: this.authority };
    if (
      !this.authority.isLoggedUserPrimary &&
      (this.authority.modifyReportbyChangeRequest.authorityChangeRequestStatus === Status.CHANGE_PROPOSED.toString() ||
        this.authority.modifyReportbyChangeRequest.authorityChangeRequestStatus === Status.CHANGE_PROPOSED_PARTIALLY_ACCEPTED.toString())
    ) {
      this.dataService.openSharedAuthorityModel(true, AppConstant.AUTHORITY_MODIFIED, item);
    } else if (this.authority.isLoggedUserPrimary) {
      if (this.authority.modifyReportbyChangeRequest.authorityChangeRequestStatus === Status.CHANGE_PROPOSED_ACCEPTED.toString()) {
        this.dataService.openSharedAuthorityModel(true, AppConstant.AUTHORITY_ACCEPTED_MODIFICATION, item);
      } else if (this.authority.modifyReportbyChangeRequest.authorityChangeRequestStatus === Status.CHANGE_DISPATCHER_APPROVED.toString()) {
        this.dataService.openSharedAuthorityModel(true, AppConstant.AUTHORITY_ACCEPTED_MODIFICATION_DISPATCHER, item);
      }
    }
  }

  requestAction(requestedAction: Status | Source): void {
    this.releaseEicList = [];
    if (this.authority.loggedInEICDetail?.primary && this.enableReleaseShareAuthorityButton && requestedAction === Status.SHARING_RELEASE) {
      this.authority.employeeInCharges.forEach(eic => {
        if (
          eic.status === EICSTatus.ACTIVE &&
          !eic.primary &&
          (!this.authority.eicChangeRequests[eic.eicUserId] ||
            (this.authority.eicChangeRequests[eic.eicUserId] &&
              this.authority.eicChangeRequests[eic.eicUserId].authorityChangeRequestStatus !== Status.UNSHARING_INITIATED.toString()))
        ) {
          this.releaseEicList.push(eic);
        }
      });
      this.reviewSharingRequest = false;
      this.releaseSharedAuthorityModelStatus = true;
      return;
    }
    this.actionRequested.emit(requestedAction);
  }

  updateOperatedUpon(operatedUpon: boolean): void {
    this.operatedUpon = operatedUpon;
  }

  getDirectionText(directionCode: string): string {
    return Direction[directionCode];
  }

  getTrackName(location: Location): string {
    if (location.track !== undefined) {
      return location.track.name;
    }
    return 'Unknown';
  }

  isActionAllowed() {
    return (this.operatedUpon =
      (this.authority.status === Status.AUTHORITY_APPROVED ||
        this.authority.status === Status.ACTIVE ||
        this.authority.status === Status.RELEASE_APPROVED) &&
      (this.authority.modifyReportbyChangeRequest.authorityChangeRequestId === undefined ||
        this.authority.modifyReportbyChangeRequest.authorityChangeRequestId === null ||
        this.authority.modifyReportbyChangeRequest.authorityChangeRequestStatus === Status.CHANGE_DISPATCHER_APPROVED.toString()));
  }

  createComment(): void {
    this.commentAuthority.emit(this.authority);
  }

  createChangeRequest(): void {
    this.changeRequest.emit(this.authority);
  }

  showEC1Number(): boolean {
    return (
      this.authority.ec1Number !== null &&
      this.authority.ec1Number !== undefined &&
      (this.authority.displayStatus === DisplayStatus.ACTIVE || this.authority.displayStatus === DisplayStatus.RELEASED)
    );
  }

  hasEffectiveDateChanged(): boolean {
    return this.authority.authorityChanges.find(item => item.name === 'effectiveDate') !== undefined;
  }

  hasExpirationDateChanged(): boolean {
    return this.authority.authorityChanges.find(item => item.name === 'expirationDate') !== undefined;
  }

  hasLineItemDirectionChanged(lineItemId: string): boolean {
    return (
      this.authority.authorityChanges.find(
        item => item.name.indexOf('Limit(' + lineItemId + ')') === 0 && item.name.indexOf('directionCode') !== -1
      ) !== undefined
    );
  }
  toggleErrorModel(value: boolean): void {
    this.eventBus.fireEvent(EventType.NOTIFY_FAILURE_EVENT, new ATMEventData(null, () => AppConstant.ERROR_MESSAGE));
  }

  toggleReleaseAuthorityModel(statusValue: boolean): void {
    this.releaseSharedAuthorityModelStatus = statusValue;
  }

  showReleaseErrorNotification(value: String): void {
    this.eventBus.fireEvent(EventType.NOTIFY_FAILURE_EVENT, new ATMEventData(null, () => value));
  }

  hideShowAuthorityMenuItem() {
    this.authority.authorityMenu[0].visible = !this.hideShareButton;
    this.authority.authorityMenu[0].disabled = !this.systemState.state;
    this.authority.authorityMenu[1].visible = this.authority.status !== Status.RELEASED;
    this.authority.authorityMenu[2].visible = !this.hideShareButton;
    this.authority.authorityMenu[2].disabled = !this.systemState.state;
    this.authority.authorityMenu[3].visible =
      this.authority.isLoggedUserPrimary && !this.hideShareButton && this.authority.enabledFeatures.enableChangeRequest &&
      !this.isReviewButtonEnable && this.shareInfoPending && this.shareInfoPending.userList.length === 0;
    if (this.systemState.state) {
      if (this.authority.authorityMenu[3].visible && this.authority.authorityMenu[3].disabled) {
        this.authority.authorityMenu[3].disabled = false;
      }
      if (this.isAdmin()) {
        this.authority.authorityMenu[3].disabled = true;
      }
    } else if (this.authority.authorityMenu[3].visible && !this.authority.authorityMenu[3].disabled) {
      this.authority.authorityMenu[3].disabled = true;
    }
  }
  onClickAuthorityMenu(event: string) {
    switch (event) {
      case AppConstant.CHANGE_HISTORY_TEXT: {
        this.dataShareService.updateAuthority(this.authority);
        this.router.navigate([AppRouteConstant.CHANGE_HISTORY_ROUTE]);
        break;
      }
      case AppConstant.RELEASE_AUTHORITY_TEXT: {
        if (!this.hideShareButton && this.systemState.state) {
          this.requestAction(Status.RELEASE_REQUESTED);
        }
        break;
      }
      case AppConstant.RELEASE_SHARED_AUTHORITY_TEXT: {
        if (!this.hideShareButton && this.systemState.state) {
          this.requestAction(Status.SHARING_RELEASE);
        }
        break;
      }
      case AppConstant.TRANSFER_TO_PAPER_TEXT: {
        this.requestAction(Source.TRANSFER_TO_PAPER);
        break;
      }
      case AppConstant.COMMENTS_TEXT: {
        if (!this.authority.authorityMenu[2].disabled) {
          this.createComment();
        }
        break;
      }
      case AppConstant.MODIFY_AUTHORITY_TEXT: {
        if (!this.authority.authorityMenu[3].disabled) {
          this.createChangeRequest();
        }
        break;
      }
    }
  }

  reviewRequest(racfId) {
    this.releaseEicList = [];
    this.authority.employeeInCharges.forEach(eic => {
      if (eic.eicUserId === racfId) {
        this.releaseEicList.push(eic);
      }
    });
    this.releaseSharedAuthorityModelStatus = true;
    this.reviewSharingRequest = true;
  }

  onClickReviewRejected() {
    const item = { authorityId: this.authority.id, authority: this.authority };
    this.dataService.openSharedAuthorityModel(true, AppConstant.AUTHORITY_REJECTED_MODIFICATION, item);
  }

  sendUnshareInitiatedRequest(userInfo: UserInfo) {
    let changeRequest: ChangeRequest = {
      trackAuthorityId: this.authority.id,
      type: ChangeRequestType.UNSHARE,
      sharingChangeModel: {
        secondaryEICRacf: userInfo.racfId
      }
    };
    this.eventBus.fireEvent(EventType.BLOCK_UI_EVENT, null);
    this.changeRequestService
      .postChangeRequest(changeRequest, this.authority, `${userInfo.racfId} - ${userInfo.employeeName}`)
      .then(result => {
        this.eventBus.fireEvent(EventType.AUTHORITY_UPDATED_EVENT, new ATMEventData(this.authority, () => { }));
        this.eventBus.fireEvent(EventType.RELEASE_UI_EVENT, null);
      });
  }

  setSharedData() {
    this.hideShareButton = this.hideShareButton || this.authority.modifyReportbyChangeRequest.authorityChangeRequestId !== undefined;
    if (this.authority.isAuthorityShared) {
      this.shareInfoPending = new ShareInfoUsersListModal();
      this.shareInfoActive = new ShareInfoUsersListModal();
      this.shareInfoReviewRequest = new ShareInfoUsersListModal();
      this.showShareInfo = true;
      if (!this.isAuthorityHistory && !this.authority.modifyReportbyChangeRequest.authorityChangeRequestId) {
        if (this.authority.isLoggedUserPrimary) {
          this.enableReleaseShareAuthorityButton = this.authority.activeSecondoriesCount > 0;
        } else {
          this.enableReleaseShareAuthorityButton = this.authority.loggedInEICDetail?.status === EICSTatus.ACTIVE ||
            (this.authority.eicChangeRequests[this.loggedInUserId] &&
              this.authority.eicChangeRequests[this.loggedInUserId].authorityChangeRequestStatus === Status.SHARED_AUTHORITY_ACCEPTED.toString());
        }
        if (this.enableReleaseShareAuthorityButton) {
          this.authority.authorityMenu[0].label = AppConstant.RELEASE_SHARED_AUTHORITY_TEXT;
        }
        if (
          !this.authority.isLoggedUserPrimary &&
          this.authority.eicChangeRequests[this.loggedInUserId] &&
          this.authority.eicChangeRequests[this.loggedInUserId].authorityChangeRequestStatus === Status.SHARING_INITIATED.toString()
        ) {
          this.isReviewSharedButtonEnable = this.authority.sourceSystemName !== Source.TRANSFER_TO_PAPER;
        }
      }
      this.showReviewModificationButton = false;

      this.setSharedDetails();
    }
  }

  setDisplayDetail(eicDetail) {
    let resultString = new UserInfo();
    resultString.racfId = eicDetail.eicUserId;
    resultString.employeeName = this.dataShareService.getNameByFirstAndLastName(eicDetail.eicEmployeeName);
    resultString.name =
      resultString.racfId +
      ' - ' +
      this.dataShareService.getNameByFirstAndLastName(eicDetail.eicEmployeeName) +
      this.getEICMilePost(eicDetail);
    return resultString;
  }
  setModificationData(eicDetail, displayData) {
    if (eicDetail.lastChangeRequestStatus === SecondaryEICModificationStatus.PENDING.toString()) {
      if (eicDetail.eicUserId === this.loggedInUserId && this.authority.sourceSystemName !== Source.TRANSFER_TO_PAPER) {
        this.showReviewModificationButton = true;
      }
      if (this.authority.isSharedModificationRejected) {
        if (!this.authority.isLoggedUserPrimary) {
          displayData.modificationStatus = AppConstant.REJECTED_MODIFICATION;
        } else {
          displayData.status = AppConstant.SHARED_STATUS_UNSHARE;
          displayData.modificationStatus = AppConstant.REJECTED_MODIFICATION + ' -';
        }
        this.shareInfoActive.userList.push(displayData);
      } else {
        displayData.status = AppConstant.SHARED_STATUS_PENDING;
        this.shareInfoPending.userList.push(displayData);
      }
    } else if (eicDetail.lastChangeRequestStatus === SecondaryEICModificationStatus.ACCEPTED.toString()) {
      if (this.authority.isSharedModificationRejected) {
        if (!this.authority.isLoggedUserPrimary) {
          displayData.modificationStatus = AppConstant.ACCEDPTED_MODIFICATIONS;
        } else {
          displayData.status = AppConstant.SHARED_STATUS_UNSHARE;
          displayData.modificationStatus = AppConstant.ACCEDPTED_MODIFICATIONS + '-';
        }
      } else {
        if (!this.authority.isLoggedUserPrimary) {
          displayData.modificationStatus = AppConstant.ACCEPTED;
        } else {
          displayData.status = AppConstant.SHARED_STATUS_UNSHARE;
          displayData.modificationStatus = AppConstant.ACCEPTED + '-';
        }
      }
      this.shareInfoActive.userList.push(displayData);
    }
  }
  setSharingData(eicDetail, displayData) {
    if (
      (this.authority.isLoggedUserPrimary &&
        (eicDetail.status === EICSTatus.PENDING.toString() || eicDetail.status === EICSTatus.ACTIVE.toString())) ||
      (this.authority.loggedInEICDetail.eicUserId === eicDetail.eicUserId && !this.isAuthorityHistory) ||
      (!this.authority.isLoggedUserPrimary && eicDetail.status === EICSTatus.ACTIVE.toString())
    ) {
      let status =
        this.authority.eicChangeRequests[eicDetail.eicUserId] &&
        this.authority.eicChangeRequests[eicDetail.eicUserId].authorityChangeRequestStatus;
      if (eicDetail.status === EICSTatus.ACTIVE && (!status || (status && status !== Status.UNSHARING_INITIATED.toString()))) {
        this.activeCount++;
      } else if (eicDetail.status === EICSTatus.PENDING || (status && status === Status.UNSHARING_INITIATED.toString())) {
        this.pendingCount++;
      }
      if (this.authority.loggedInEICDetail.eicUserId === eicDetail.eicUserId) {
        this.setSecondaryData(eicDetail, status, displayData);
      } else {
        if (this.authority.isLoggedUserPrimary && status === Status.SHARING_REQUESTED.toString()) {
          displayData.status = AppConstant.SHARED_STATUS_REVIEW_REQUEST;
          this.shareInfoReviewRequest.userList.push(displayData);
          this.isReviewButtonEnable = this.authority.sourceSystemName !== Source.TRANSFER_TO_PAPER;
        } else if (status === Status.SHARING_INITIATED.toString() || status === Status.UNSHARING_INITIATED.toString()) {
          if (status === Status.UNSHARING_INITIATED.toString() && this.isAuthorityHistory) {
            this.shareInfoActive.userList.push(displayData);
          } else {
            displayData.status = AppConstant.SHARED_STATUS_PENDING;
            this.shareInfoPending.userList.push(displayData);
          }
        } else if (eicDetail.status === EICSTatus.ACTIVE) {
          if (this.authority.isLoggedUserPrimary) {
            displayData.status = AppConstant.SHARED_STATUS_UNSHARE;
          }
          this.shareInfoActive.userList.push(displayData);
        }
      }
    }
  }

  setSecondaryData(eicDetail, status, display) {
    if (
      status === Status.SHARING_INITIATED.toString() ||
      status === Status.UNSHARING_INITIATED.toString() ||
      status === Status.SHARING_REQUESTED.toString()
    ) {
      this.isSecondaryRequestPending = status === Status.SHARING_REQUESTED.toString();
      display.status = AppConstant.SHARED_STATUS_PENDING;
      this.shareInfoPending.userList.push(display);
      this.hideShareButton = this.hideShareButton || status !== Status.UNSHARING_INITIATED.toString();
    } else if (eicDetail.status === EICSTatus.ACTIVE) {
      this.shareInfoActive.userList.push(display);
    }
  }

  resetSharedFlags() {
    this.showShareInfo = false;
    this.enableReleaseShareAuthorityButton = false;
    this.isReviewButtonEnable = false;
    this.isReviewSharedButtonEnable = false;
    this.isSecondaryRequestPending = false;
    this.showReviewModificationButton = false;
    this.hideShareButton = false;
    this.authority.authorityMenu[0].label = AppConstant.RELEASE_AUTHORITY_TEXT;
  }

  private handleUpdateSuccess(updatedAuthority: ITrackAuthority, disableFlash: boolean) {
    if (updatedAuthority.id === this.authority.id) {
      this.isTTPSuccessFul = false;
      if (updatedAuthority.sourceSystemName === Source.TRANSFER_TO_PAPER) {
        this.isTTPSuccessFul = true;
      }
      if (!disableFlash) {
        this.updated = true;
        setTimeout(() => (this.updated = false), 1000);
      }
    }
  }

  private setSharedDetails() {
    if (
      this.authority.isLoggedUserPrimary &&
      this.authority.sourceSystemName !== Source.TRANSFER_TO_PAPER &&
      this.authority.modifyReportbyChangeRequest.authorityChangeRequestStatus === Status.CHANGE_PROPOSED_ACCEPTED.toString()
    ) {
      this.showReviewModificationButton = true;
    }
    if (this.authority.employeeInCharges.length > 1) {
      this.activeCount = 0;
      this.pendingCount = 0;
      for (let i = 0; i < this.authority.employeeInCharges.length; i++) {
        if (!this.authority.employeeInCharges[i].primary) {
          if (!this.authority.loggedInEICDetail) {
            break;
          }
          if (
            this.authority.loggedInEICDetail.status === EICSTatus.REJECTED.toString() ||
            this.authority.loggedInEICDetail.status === EICSTatus.RELEASED.toString() ||
            this.authority.loggedInEICDetail.status === EICSTatus.CANCELLED.toString()
          ) {
            if (this.authority.loggedInEICDetail.eicUserId === this.authority.employeeInCharges[i].eicUserId) {
              break;
            } else {
              continue;
            }
          }
          let display: UserInfo = this.setDisplayDetail(this.authority.employeeInCharges[i]);
          if (this.authority.modifyReportbyChangeRequest.authorityChangeRequestId) {
            this.setModificationData(this.authority.employeeInCharges[i], display);
          } else {
            this.setSharingData(this.authority.employeeInCharges[i], display);
          }
        }
      }
    }
    if (this.authority.isAuthorityShared && (this.activeCount === 0 && this.pendingCount > 0) && this.authority.isLoggedUserPrimary) {
      this.hideShareButton = this.hideShareButton || true;
    }
  }

  private checkMobileViewPendingStatus() {
    return (
      this.authority.sourceSystemName !== Source.TRANSFER_TO_PAPER &&
      (AppConstant.AWAITING_RESPONSE_MODIFICATION_STATUS.indexOf(this.authority.status) >= 0 ||
        AppConstant.AWAITING_RESPONSE_RELEASED_AUTHORITY_STATUS.indexOf(this.authority.status) >= 0 ||
        this.statusArray
          .map(arr => arr.toString())
          .indexOf(this.authority.modifyReportbyChangeRequest && this.authority.modifyReportbyChangeRequest.authorityChangeRequestStatus) >=
        0 ||
        this.authority.modifyReportbyChangeRequest.authorityChangeRequestStatus === Status.CHANGE_DISPATCHER_APPROVED.toString() ||
        this.sharedAwaitingReponse ||
        this.isReviewSharedButtonEnable ||
        this.isReviewButtonEnable ||
        this.showReviewModificationButton ||
        this.authority.isSharedModificationRejected ||
        Status.AUTHORITY_REJECTED === this.authority.status ||
        Status.AUTHORITY_APPROVED === this.authority.status ||
        Status.RELEASE_APPROVED === this.authority.status)
    );
  }

  getEICMilePost(employeeInCharge: EmployeeInCharge) {
    if (employeeInCharge.entryMilepostPrefix) {
      return ' (' + employeeInCharge.entryMilepostPrefix + ' ' + employeeInCharge.entryMilepostId + ') ';
    }
    return '';
  }

  getCancelItems(): Array<ChangeRequestComment> {
    return this.authority.modifyReportbyChangeRequest.changeUserAcceptedMessages.filter(crcomment =>
      this.isChangeOfType(crcomment, [ChangeRequestCommentType.CANCELEDLINE])
    );
  }

  getDirectionChangeItems(): Array<ChangeRequestComment> {
    return this.authority.modifyReportbyChangeRequest.changeUserAcceptedMessages.filter(crcomment =>
      this.isChangeOfType(crcomment, [ChangeRequestCommentType.DIRECTIONCHANGE])
    );
  }

  getTimeExtensionItems(): Array<ChangeRequestComment> {
    return this.authority.modifyReportbyChangeRequest.changeUserAcceptedMessages.filter(crcomment =>
      this.isChangeOfType(crcomment, [ChangeRequestCommentType.UNTILTIMEEXTENDED])
    );
  }

  getReportByItem(): Array<ChangeRequestComment> {
    return this.authority.modifyReportbyChangeRequest.changeUserAcceptedMessages.filter(crcomment =>
      this.isChangeOfType(crcomment, [ChangeRequestCommentType.REPORTBY])
    );
  }

  isChangeOfType(comment: ChangeRequestComment, commentTypeList: ChangeRequestCommentType[]): boolean {
    for (let commentType of commentTypeList) {
      if (ChangeRequestCommentType[commentType] === comment.type.toString()) {
        return true;
      }
    }
    return false;
  }

  getChangeMessages(): Array<ChangeRequestComment> {
    return this.authority.modifyReportbyChangeRequest.changeUserAcceptedMessages;
  }

  isAdmin() {
    return this.proxy.racf !== undefined && this.authService.isRoleAuthorized(['client.atam.admin']) &&
      !this.authService.isRoleAuthorized(['client.atam.developer']);
  }

  checkPrimaryRelease(){
    if(this.authority.isLoggedUserPrimary){
      return true
    } else{
     return [
        EICSTatus.RELEASED,
        EICSTatus.REJECTED,
        EICSTatus.CANCELLED].indexOf(this.authority.loggedInEICDetail.status) < 0 ;
    }
  }
}
